// ToastContext — transient bottom-of-screen status messages.
//
// Pre-extraction, App.jsx owned both the toast state (useState + a setTimeout
// dismissal) and the showToast callback that ~6 sites called via prop
// drilling. Even more awkwardly, useCloudSync wanted to fire showToast for
// the cloud-offline notice, but App declared the toast state AFTER the
// useCloudSync hook — the workaround was a ref-captured callback that got
// patched in via a follow-up useEffect. Hoisting toast into a context
// breaks that ordering knot: useCloudSync can call useToast() directly.
//
// Caller pattern (App):
//   <ToastProvider>
//     <App />
//   </ToastProvider>
//
// Consumer pattern (any screen / hook):
//   const { showToast } = useToast();
//   showToast("Saved.", "info");

import { createContext, useCallback, useContext, useRef, useState } from "react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  // Track active dismissal timer so a fresh showToast cancels the prior
  // one (otherwise back-to-back messages step on each other's countdown).
  const timerRef = useRef(null);

  const showToast = useCallback((message, kind = "info", durationMs = 4000) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ message, kind });
    if (durationMs > 0) {
      timerRef.current = setTimeout(() => setToast(null), durationMs);
    }
  }, []);

  const dismissToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * Returns { toast, showToast, dismissToast }.
 * Throws if called outside a ToastProvider — toast fire-and-forget needs
 * a real provider above, otherwise the message would silently disappear.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() must be used within a ToastProvider");
  return ctx;
}
